import type { FC } from 'react';
import { useEffect } from 'react';

import { boxOf, describeDrop, findInsertMarker, flowOf, ghostFor, labelFor, unionOf } from './drop-feedback';
import { createDropLayer } from './drop-layer';

/** Sanity's own attribute: blocks sharing a value are one list the overlay lets you reorder. */
const DRAG_GROUP = 'data-sanity-drag-group';

const dragSourceAt = (x: number, y: number): Element | undefined => {
  // The overlay sits on top of the page, so the block is the first hit underneath it that is draggable.
  const hit = document.elementsFromPoint?.(x, y).find(element => element.closest(`[${DRAG_GROUP}]`));
  return hit?.closest(`[${DRAG_GROUP}]`) ?? undefined;
};

/**
 * Shows where a dragged block will land, how much room it takes, and whether the drop changes anything.
 *
 * Sanity draws a thin bar between two blocks and nothing more, which leaves the editor guessing at the
 * size of the hole they are about to make. This adds the missing half (see `drop-feedback.ts`).
 *
 * It does not start, steer or finish a drag: the overlay does all of that. This only watches. A press on
 * a block of a draggable group starts it looking; while the overlay shows its insert bar, it draws the
 * landing next to it; releasing, Escape, losing focus, or a move with no button down clears it. A plain click never sees a
 * bar, so draws nothing. If the overlay's bar cannot be found, it draws nothing and Sanity's own stays.
 *
 * Renders nothing, and only runs in draft mode (the render host mounts it beside the overlay).
 */
export const DropFeedback: FC = () => {
  useEffect(() => {
    const layer = createDropLayer();
    let source: Element | undefined;
    let frame: number | undefined;

    const stop = (): void => {
      source = undefined;
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = undefined;
      layer.hide();
    };

    const draw = (dragged: Element): void => {
      const marker = findInsertMarker();
      const group = dragged.getAttribute(DRAG_GROUP);
      if (!marker || !group) {
        layer.hide();
        return;
      }

      const members = Array.from(document.querySelectorAll(`[${DRAG_GROUP}]`)).filter(
        element => element.getAttribute(DRAG_GROUP) === group,
      );
      const origin = boxOf(dragged.getBoundingClientRect());
      const bar = boxOf(marker.getBoundingClientRect());
      const flow = flowOf(bar);
      const others = members.filter(member => member !== dragged).map(member => boxOf(member.getBoundingClientRect()));
      const description = describeDrop(others, origin, bar, flow);

      layer.show({
        group: unionOf([origin, ...others]) ?? origin,
        origin,
        ghost: ghostFor(bar, origin, flow, { width: window.innerWidth, height: window.innerHeight }),
        label: labelFor(description),
        isUnchanged: description.isUnchanged,
      });
    };

    const track = (): void => {
      if (!source?.isConnected) {
        stop();
        return;
      }
      draw(source);
      frame = window.requestAnimationFrame(track);
    };

    const onPress = (event: MouseEvent): void => {
      stop();
      if (event.button !== 0) return;
      source = dragSourceAt(event.clientX, event.clientY);
      if (source) frame = window.requestAnimationFrame(track);
    };

    // A release outside the window can go unreported; a move with no button down proves the drag is over.
    const onMove = (event: MouseEvent): void => {
      if (source && event.buttons === 0) stop();
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') stop();
    };

    // Capture, so nothing the overlay does with the event can hide it from here.
    window.addEventListener('mousedown', onPress, true);
    window.addEventListener('mouseup', stop, true);
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', stop);
    return () => {
      window.removeEventListener('mousedown', onPress, true);
      window.removeEventListener('mouseup', stop, true);
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', stop);
      stop();
    };
  }, []);

  return null;
};
